import { useState } from 'react'
import {
  AlertCircle,
  ArrowRightCircle,
  CheckCircle2,
  CreditCardIcon,
  Wallet,
} from 'lucide-react'

import { Button } from '@/components'
import { cn } from '@/utils/cn'

type PaymentStatus = 'paid' | 'pending' | 'trial'

interface Payment {
  id: string
  childName: string
  grade: string
  courseTitle: string
  amount: number
  currency: string
  date: string
  dueDate?: string
  status: PaymentStatus
  statusLabel: string
  method: string
}

const PAYMENTS: Payment[] = [
  {
    id: '1',
    childName: 'Sarah Johnson',
    grade: 'Grade 5',
    courseTitle: 'Python for Beginners',
    amount: 119,
    currency: '$',
    date: 'Nov 28',
    status: 'paid',
    statusLabel: 'Paid',
    method: 'Card · **** 4321',
  },
  {
    id: '2',
    childName: 'Sarah Johnson',
    grade: 'Grade 5',
    courseTitle: 'Python for Beginners (Next month)',
    amount: 119,
    currency: '$',
    date: 'Due Dec 8',
    dueDate: 'Dec 8',
    status: 'pending',
    statusLabel: 'Pending',
    method: 'Auto-debit paused',
  },
  {
    id: '3',
    childName: 'Michael Johnson',
    grade: 'Grade 5',
    courseTitle: 'Web Development (Trial)',
    amount: 0,
    currency: '$',
    date: 'Trial · 1 class left',
    status: 'trial',
    statusLabel: 'Trial',
    method: 'Trial class',
  },
]

const STATUS_STYLES: Record<
  PaymentStatus,
  { bg: string; text: string; dot: string }
> = {
  paid: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    dot: 'bg-emerald-500',
  },
  pending: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    dot: 'bg-amber-400',
  },
  trial: {
    bg: 'bg-sky-100',
    text: 'text-sky-800',
    dot: 'bg-sky-400',
  },
}

export const ParentPayments: React.FC = () => {
  const [invoiceFilter, setInvoiceFilter] = useState<
    'all' | 'paid' | 'pending'
  >('all')

  const totalPaid = PAYMENTS.filter(p => p.status === 'paid').reduce(
    (sum, p) => sum + p.amount,
    0
  )
  const totalPending = PAYMENTS.filter(p => p.status === 'pending').reduce(
    (sum, p) => sum + p.amount,
    0
  )

  const nextDuePayment = PAYMENTS.find(p => p.status === 'pending')
  const nextDueDate = nextDuePayment?.dueDate ?? '—'

  const filteredInvoices = PAYMENTS.filter(payment => {
    if (invoiceFilter === 'all') {
      return payment.status === 'paid' || payment.status === 'pending'
    }
    return payment.status === invoiceFilter
  })

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-100 p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Payments</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage payment history and methods.
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            icon={<CreditCardIcon className="h-4 w-4" />}
            className="self-start bg-[#F28C28] px-4 py-2 !rounded-sm text-sm font-medium hover:bg-portal-orange-hover"
          >
            Add payment method
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="flex items-center justify-between gap-4 rounded-2xl border-2 border-[#D0EAD9] bg-[#F4F8F7] p-6">
            <div>
              <p className="text-xs text-gray-500">Total paid so far</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                ${totalPaid.toFixed(2)}
              </p>
              <span className="text-xs font-normal text-[#717392]">
                This year
              </span>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E2F1E8] text-[#34A853]">
              <CheckCircle2 className="h-3 w-3" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border-2 border-[#F7E0C9] bg-[#F9F7F5] p-6">
            <div>
              <p className="text-xs text-gray-500">Pending amount</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                ${totalPending.toFixed(2)}
              </p>
              <span className="text-xs font-normal text-[#717392]">
                Due soon
              </span>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F8EDE3] text-[#F28C28]">
              <AlertCircle className="h-3 w-3" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border-2 border-[#C7D5E8] bg-[#F5F7F9] p-6">
            <div>
              <p className="text-xs text-gray-500">Next due date</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {nextDueDate}
              </p>
              <span className="text-xs font-normal text-[#717392]">
                $119.00
              </span>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E1E8F2] text-[#1D5AA6]">
              <Wallet className="h-3 w-3" />
            </div>
          </div>
        </div>

        {/* Payment methods */}
        <div className="space-y-3 bg-white rounded-2xl border-2 border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Payment methods
            </h3>
            <button
              type="button"
              className="text-xs font-medium text-black border border-gray-100 px-4 py-1.5 rounded-sm hover:bg-gray-100"
            >
              Manage
            </button>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-1 flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between !mt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-16 items-center justify-center rounded-lg bg-white text-sm font-semibold text-gray-800 shadow-sm">
                  VISA
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Visa •••• 4242
                  </p>
                  <p className="text-[11px] text-gray-500">Expires 12/2026 </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 sm:mt-0">
                <span className="inline-flex items-center rounded-md bg-[#34A853] px-3 py-1 text-[11px] font-medium text-white">
                  Default
                </span>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between !mt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-16 items-center justify-center rounded-lg bg-white text-sm font-semibold text-gray-800 shadow-sm">
                  VISA
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Visa •••• 4242
                  </p>
                  <p className="text-[11px] text-gray-500">Expires 12/2026 </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices list */}
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100">
            <div className="flex gap-6 text-sm">
              <button
                type="button"
                className={cn(
                  'pb-2 border-b-2 font-medium',
                  invoiceFilter === 'all'
                    ? 'border-[#F28C28] text-[#F28C28]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
                onClick={() => setInvoiceFilter('all')}
              >
                All Payments
              </button>
              <button
                type="button"
                className={cn(
                  'pb-2 border-b-2 font-medium',
                  invoiceFilter === 'paid'
                    ? 'border-[#34A853] text-[#34A853]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
                onClick={() => setInvoiceFilter('paid')}
              >
                Paid
              </button>
              <button
                type="button"
                className={cn(
                  'pb-2 border-b-2 font-medium',
                  invoiceFilter === 'pending'
                    ? 'border-[#F28C28] text-[#F28C28]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
                onClick={() => setInvoiceFilter('pending')}
              >
                Pending
              </button>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            {filteredInvoices.map(payment => {
              const styles = STATUS_STYLES[payment.status]

              return (
                <div
                  key={payment.id}
                  className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-center"
                >
                  {/* Left: child + course */}
                  <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex shrink-0 items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-800 shadow-sm">
                        {payment.childName
                          .split(' ')
                          .map(n => n[0])
                          .join('')}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {payment.childName}
                        </p>
                        <p className="text-xs text-gray-500">{payment.grade}</p>
                        <p className="mt-2 text-xs font-medium text-gray-800">
                          {payment.courseTitle}
                        </p>
                        <p className="mt-1 text-[11px] text-gray-500">
                          {payment.method}
                        </p>
                      </div>
                    </div>

                    {/* Middle: amount + dates */}
                    <div className="mt-3 w-full max-w-xs sm:mt-0 sm:pl-4">
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {payment.currency}
                        {payment.amount.toFixed(2)}
                      </p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {payment.status === 'pending'
                          ? `Due: ${payment.dueDate}`
                          : payment.date}
                      </p>
                    </div>
                  </div>

                  {/* Right: status + actions */}
                  <div className="flex flex-col items-start gap-3 sm:items-end">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium',
                        styles.bg,
                        styles.text
                      )}
                    >
                      <span
                        className={cn('h-1.5 w-1.5 rounded-full', styles.dot)}
                      />
                      {payment.statusLabel}
                    </span>

                    <div className="flex flex-wrap gap-2">
                      {payment.status === 'pending' ? (
                        <Button
                          type="button"
                          variant="primary"
                          icon={<ArrowRightCircle className="h-4 w-4" />}
                          className="bg-[#F28C28] px-3 py-2 text-xs font-semibold hover:bg-portal-orange-hover focus:ring-portal-orange"
                        >
                          Pay now
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          className="border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Download receipt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
